// formchrome.ts — shared faithful chrome for the DOM UI shells (menu / lobby /
// in-game forms). The first shared piece is the ShellForm form-open ZIP.
//
// The canvas ShellForm zip (game/Shell.cpp ShellForm::ShellDoModal fAnimate +
// OnTimer; ts: shellform.ts:298-384): when a form opens, EVERY control flies to
// its place from an alternating side — odd index from the LEFT, even from the
// RIGHT (m_axSrc = i&1 ? -width : siz.cx, shellform.ts:325) — staggered
// kctZipLag (=3 ticks = 30ms) apart over a kctZip (=25 ticks = 250ms) LINEAR
// slide, and ksfxGuiFormShow plays once the last control lands (kfFrmShowSound).
//
// The menu/lobby originally zipped only their buttons (a 130% nudge); that was a
// downgrade — the real zip cascades ALL the form's controls. This helper is the
// faithful version, shared so menu-view / lobby-view / objectives-view don't drift.

import { ksfxGuiFormShow } from '../ui/uikit.ts';
import { prefersReducedMotion } from './chrome.ts';

const kctZipMs = 250; // shellform.ts kctZip = 25 ticks
const kctZipLagMs = 30; // shellform.ts kctZipLag = 3 ticks

/** Cascade a design stage's TOP-LEVEL controls in (the ShellForm form-open zip).
 *  Each DIRECT child of `stage` slides to translateX(0) from an alternating window
 *  edge (odd → left, even → right), staggered kctZipLagMs apart over a kctZipMs
 *  linear slide; `playSfx(ksfxGuiFormShow)` fires when the last control lands.
 *  A LIST control is a single child here, so it zips as one unit (matching the
 *  canvas ListControl — its rows do NOT cascade individually). The host root must
 *  clip (overflow:hidden) so the off-window start positions don't flash a scrollbar.
 *  Returns the sfx timer so the caller can cancel it on re-zip / unmount.
 *
 *  ACCESSIBILITY: under prefers-reduced-motion:reduce the slide/stagger is SKIPPED —
 *  the controls are placed directly (no off-window start, no transition), and the
 *  ksfxGuiFormShow still plays at the end (the form-shown cue is audio, not motion).
 *  One guard at the entry point covers every shell view's zip. */
export function zipInStage(
  stage: HTMLElement,
  playSfx?: (sfx: number) => void,
): ReturnType<typeof setTimeout> {
  if (prefersReducedMotion()) {
    // controls appear in place (no slide/stagger); the form-shown sfx still fires.
    return setTimeout(() => {
      playSfx?.(ksfxGuiFormShow);
    }, 0);
  }
  const els = [...stage.children] as HTMLElement[];
  const off = window.innerWidth; // start off the window edge (canvas: m_axSrc siz.cx / -width)
  els.forEach((e, i) => {
    e.style.transform = `translateX(${i % 2 === 0 ? off : -off}px)`;
  });
  void stage.offsetWidth; // commit the start positions before transitioning
  els.forEach((e, i) => {
    e.style.transition = `transform ${kctZipMs}ms linear ${i * kctZipLagMs}ms`;
    e.style.transform = 'translateX(0)';
  });
  return setTimeout(
    () => {
      playSfx?.(ksfxGuiFormShow);
    },
    kctZipMs + els.length * kctZipLagMs,
  );
}
